/**
 * @fileoverview NativeSelect - Native HTML select element
 *
 * Purpose: Simple native select dropdown (lighter than Radix Select)
 * Dependencies: None (native HTML select)
 * Used by: Simple dropdowns, filters, settings where custom styling isn't critical
 *
 * Features:
 * - Native browser select element
 * - Consistent styling with design system
 * - Lighter weight than Radix Select
 * - Works without JavaScript
 * - Accessible by default
 * - Focus states
 *
 * @example
 * <NativeSelect>
 *   <option value="">Select an option</option>
 *   <option value="1">Option 1</option>
 *   <option value="2">Option 2</option>
 * </NativeSelect>
 *
 * @example
 * // With label
 * <div className="space-y-2">
 *   <Label htmlFor="country">Country</Label>
 *   <NativeSelect id="country">
 *     <option>United States</option>
 *     <option>Canada</option>
 *     <option>Mexico</option>
 *   </NativeSelect>
 * </div>
 */

import * as React from "react";
import { cn } from "@/utils/misc";

export interface NativeSelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {}

const NativeSelect = React.forwardRef<HTMLSelectElement, NativeSelectProps>(
  ({ className, ...props }, ref) => {
    return (
      <select
        className={cn(
          "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-50",
          "[&>option]:bg-background [&>option]:text-foreground",
          className,
        )}
        ref={ref}
        {...props}
      />
    );
  },
);
NativeSelect.displayName = "NativeSelect";

export { NativeSelect };
